// Geometria pura da grade de widgets. Um retângulo é { position, width, height },
// onde `position` é a célula (row * columns + col) do canto superior esquerdo.

// Classes Tailwind LITERAIS (o JIT só enxerga o que está escrito por inteiro).
export const GRID_COLS_CLASSES = {
  1: 'lg:grid-cols-1',
  2: 'lg:grid-cols-2',
  3: 'lg:grid-cols-3',
  4: 'lg:grid-cols-4',
  5: 'lg:grid-cols-5',
  6: 'lg:grid-cols-6',
};

export const COL_START_CLASSES = {
  1: 'lg:col-start-1',
  2: 'lg:col-start-2',
  3: 'lg:col-start-3',
  4: 'lg:col-start-4',
  5: 'lg:col-start-5',
  6: 'lg:col-start-6',
};

export const COL_SPAN_CLASSES = {
  1: 'lg:col-span-1',
  2: 'lg:col-span-2',
  3: 'lg:col-span-3',
  4: 'lg:col-span-4',
  5: 'lg:col-span-5',
  6: 'lg:col-span-6',
};

export const ROW_START_CLASSES = {
  1: 'lg:row-start-1',
  2: 'lg:row-start-2',
  3: 'lg:row-start-3',
  4: 'lg:row-start-4',
  5: 'lg:row-start-5',
  6: 'lg:row-start-6',
};

export const ROW_SPAN_CLASSES = {
  1: 'lg:row-span-1',
  2: 'lg:row-span-2',
  3: 'lg:row-span-3',
  4: 'lg:row-span-4',
  5: 'lg:row-span-5',
  6: 'lg:row-span-6',
};

export const cellToRowCol = (cell, columns) => ({
  row: Math.floor(cell / columns),
  col: cell % columns,
});

export const rowColToCell = (row, col, columns) => row * columns + col;

export const rectCells = ({ position, width, height }, columns) => {
  const { row, col } = cellToRowCol(position, columns);
  const cells = [];
  for (let r = row; r < row + height; r += 1) {
    for (let c = col; c < col + width; c += 1) {
      cells.push(rowColToCell(r, c, columns));
    }
  }
  return cells;
};

export const fitsInGrid = (rect, columns, rows) => {
  const { row, col } = cellToRowCol(rect.position, columns);
  return (
    rect.position >= 0 &&
    rect.width >= 1 &&
    rect.height >= 1 &&
    col + rect.width <= columns &&
    row + rect.height <= rows
  );
};

export const overlaps = (a, b, columns) => {
  const cellsOfB = new Set(rectCells(b, columns));
  return rectCells(a, columns).some(cell => cellsOfB.has(cell));
};

export const canPlace = (rect, others, columns, rows) =>
  fitsInGrid(rect, columns, rows) &&
  !others.some(other => overlaps(rect, other, columns));

// Widget (que não seja o próprio) que cobre a célula.
export const widgetAtCell = (cell, widgets, columns, ignoreId) =>
  widgets.find(
    widget =>
      widget.id !== ignoreId && rectCells(widget, columns).includes(cell)
  );

// Soltar sobre um widget de MESMO tamanho troca os dois de lugar.
export const findSwapTarget = (movedRect, targetCell, widgets, columns) => {
  const target = widgetAtCell(targetCell, widgets, columns, movedRect.id);
  const sameSize =
    target &&
    target.width === movedRect.width &&
    target.height === movedRect.height;
  return sameSize ? target : undefined;
};

// Maior tamanho que o widget alcança sobre células livres: 'right' devolve a
// largura máxima, 'bottom' a altura máxima e 'corner' as duas (cada eixo por si).
export const maxResize = (widget, direction, others, columns, rows) => {
  const free = others.filter(other => other.id !== widget.id);
  const grow = key => {
    let size = widget[key];
    while (canPlace({ ...widget, [key]: size + 1 }, free, columns, rows)) {
      size += 1;
    }
    return size;
  };
  return {
    width: direction === 'bottom' ? widget.width : grow('width'),
    height: direction === 'right' ? widget.height : grow('height'),
  };
};

// Tamanho final de um arrasto: o desejado, limitado ao mínimo 1×1 e ao que cabe
// sobre células livres (encolher sempre é permitido).
export const resizeTo = (widget, direction, desired, others, columns, rows) => {
  const wantsWidth = direction !== 'bottom';
  const wantsHeight = direction !== 'right';
  const base = {
    ...widget,
    width: wantsWidth
      ? Math.max(1, Math.min(desired.width, widget.width))
      : widget.width,
    height: wantsHeight
      ? Math.max(1, Math.min(desired.height, widget.height))
      : widget.height,
  };
  const { width: maxWidth } = maxResize(base, 'right', others, columns, rows);
  const width = wantsWidth
    ? Math.max(base.width, Math.min(desired.width, maxWidth))
    : base.width;
  const { height: maxHeight } = maxResize(
    { ...base, width },
    'bottom',
    others,
    columns,
    rows
  );
  const height = wantsHeight
    ? Math.max(base.height, Math.min(desired.height, maxHeight))
    : base.height;
  return { width, height };
};

export const freeCells = (widgets, columns, rows) => {
  const used = new Set(widgets.flatMap(widget => rectCells(widget, columns)));
  return Array.from({ length: columns * rows }, (_item, cell) => cell).filter(
    cell => !used.has(cell)
  );
};
